import type { ReactNode } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { StatusDot } from "@/components/brand/status-dot";
import { Label } from "@/components/ui/label";
import type { Stage, StageStatus } from "../domain/stages.ts";

export const STAGE_LABELS: Record<Stage, { title: string; hint: string }> = {
  avatar: { title: "Avatar", hint: "Pick a published studio avatar" },
  identity: { title: "Identity", hint: "Name and job title" },
  knowledge: { title: "Knowledge", hint: "Documents, links and videos" },
  interview: { title: "Interview", hint: "Talk it through by voice" },
  distill: { title: "Distill", hint: "Turn it into a context" },
  review: { title: "Review", hint: "Check and edit the context" },
  publish: { title: "Publish", hint: "Cut an immutable release" },
  assign: { title: "Assign", hint: "Choose which tenants can hire" },
};

export function stageDot(status: StageStatus, label?: string) {
  if (status === "done") return <StatusDot tone="success" label={label ?? "Done"} />;
  if (status === "open") return <StatusDot tone="info" label={label ?? "Ready"} />;
  return <StatusDot tone="neutral" label={label ?? "Locked"} />;
}

export function sourceDot(status: string) {
  if (status === "ready") return <StatusDot tone="success" label="Ready" />;
  if (status === "failed") return <StatusDot tone="danger" label="Failed" />;
  if (status === "extracting") return <StatusDot tone="info" label="Reading" pulse />;
  return <StatusDot tone="neutral" label="Queued" pulse />;
}

export function ErrorNote({ error }: { error?: string | null }) {
  if (!error) return null;
  return (
    <Alert variant="destructive">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  );
}

export function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      {children}
    </div>
  );
}

export function SectionTitle({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {description && <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </div>
  );
}

export const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function formatWhen(iso: string | null | undefined) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
